<script>
import configService from "@/services/config";
import vueDropzone from "vue2-dropzone";
import "vue2-dropzone/dist/vue2Dropzone.min.css";
import authService from "@/services/auth";

export default {
  name: "FileUpload",

  components: {
    vueDropzone,
  },

  props: {
    dropzoneOptions: {
      type: Object,
      default: () => ({}),
    },
    type: String,
    url: {
      type: String,
      required: true,
    },
    files: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: "default",
    },
  },

  data() {
    return {
      options: {
        url: this.url,
        autoProcessQueue: false,
        addRemoveLinks: true,
        autoQueue: false,
        headers: { Authorization: `Bearer ${authService.getToken()}` },
        maxFiles: null,
        maxFilesize: 3,
        dictDefaultMessage:
          "<i class='fa fa-cloud-upload' style='margin-right: 5px'></i>Arrastrar archivos para subir o hacer click aquí",
        dictRemoveFile: "Eliminar archivo",
        dictFallbackMessage: "Su navegador no soporta arrastrar archivos",
        dictFallbackText:
          "Por favor use el formulario debajo para subir archivos",
        dictFileTooBig:
          "El archivo es muy grande ({{filesize}}MiB). Tamaño máximo: {{maxFilesize}}MiB.",
        dictInvalidFileType: "No puedes subir archivos de este tipo",
        dictResponseError: "Servidor respondió con código {{statusCode}}",
        dictCancelUpload: "Cancelar subida",
        dictUploadCanceled: "Se canceló la subida de imágenes",
        dictCancelUploadConfirmation:
          "¿Está seguro que quiere cancelar la subida?",
        dictMaxFilesExceeded: "No puedes subir más archivos",
        ...this.dropzoneOptions,
      },
      previewFiles: [],
    };
  },

  watch: {
    type: {
      immediate: true,
      handler(type) {
        const types = {
          image: "image/*",
          audio: ".mp3",
          video:
            "video/mp4,video/x-flv,video/quicktime,video/x-msvideo,video/x-ms-wmv",
          file: "application/pdf",
          csv: "text/csv",
          csvAndMedia:
            "image/*,video/mp4,video/x-flv,video/quicktime,video/x-msvideo,video/x-ms-wmv,.mp3,text/csv",
        };
        this.options.acceptedFiles = types[type] || null;

        if (this.$refs.uploader) {
          this.$refs.uploader.setOption(
            "acceptedFiles",
            this.options.acceptedFiles
          );
        }
      },
    },

    url(newURL) {
      if (this.$refs.uploader) {
        this.$refs.uploader.setOption("url", newURL);
        this.$refs.uploader.dropzone.options.url = newURL;
      }
      this.options.url = newURL;
    },

    files(newFiles) {
      // BUG-Z2V55Y (Federico, 2026-06-25): el watcher anterior llamaba
      // addFiles(newFiles) que internamente emite "addedfile" en Dropzone,
      // que vuelve a triggear emit "file-added" del componente, que el
      // padre (Indications.vue onFilesUpdated) responde reasignando el
      // objeto indication entero, lo cual dispara este watcher otra vez
      // → INFINITE LOOP → freeze del browser.
      //
      // Fix estructural: filtrar `newFiles` antes de pasarlos a addFiles.
      // Si un file ya está en dropzone.files (matcheando por `key` si lo
      // tiene, o por name+size si es local sin key), NO lo procesamos —
      // ya está renderizado. Eso rompe el loop sin necesidad de flags
      // temporales (que tenían su propio bug de timing con el nextTick
      // del padre).
      if (!this.$refs.uploader?.dropzone) return;
      const dzFiles = this.$refs.uploader.dropzone.files || [];

      if (!newFiles || newFiles.length === 0) {
        // Solo limpiar si no hay archivos agregados manualmente por el
        // usuario y tampoco hay archivos del servidor (con key)
        const hasUserFiles = this.previewFiles.some((f) => !f.key);
        const hasServerFiles = this.previewFiles.some((f) => f.key);
        if (!hasUserFiles && !hasServerFiles) {
          this.$refs.uploader.dropzone.removeAllFiles(true);
          this.previewFiles = [];
          this.$emit("update:files", []);
        }
        return;
      }

      // Filtrar files que NO están ya en dropzone (los nuevos)
      const notYetInDropzone = newFiles.filter((f) => {
        const fKey = f.key || f.value || f.path;
        if (fKey) {
          // File del server (con key) → comparar por key
          return !dzFiles.some((df) => df.key === fKey);
        }
        // File local (sin key) → comparar por name+size para evitar dup
        return !dzFiles.some(
          (df) => !df.key && df.name === f.name && df.size === f.size
        );
      });

      if (notYetInDropzone.length > 0) {
        this.addFiles(notYetInDropzone);
      }
    },
  },

  mounted() {
    this.addFiles(this.files);
  },

  methods: {
    /**
     * @description Abre el file picker del SO. BUG-Z2V55Y re-open (Federico,
     *              2026-06-25): el approach previo dependía de
     *              `dropzone.hiddenFileInput?.click()`. Cuando el componente
     *              corre en modo botón (`mode != "dropzone"`), el `<vue-dropzone>`
     *              está montado con `v-show="false"` — vue2-dropzone NO siempre
     *              inicializa `hiddenFileInput` en ese caso. El optional-chain
     *              quedaba en no-op silencioso (sin error, sin file picker, sin
     *              nada — la atención "no hace nada" al clickear).
     *
     *              Fix: crear un `<input type="file">` ad-hoc en cada click —
     *              independiente del estado interno de dropzone. Una vez que
     *              el usuario elige archivos, los agregamos a la queue de
     *              Dropzone con `addFile()` para que el flow existente
     *              (preview, enqueue, upload) siga funcionando.
     */
    openFileDialog(event) {
      event.preventDefault();
      try {
        const input = document.createElement("input");
        input.type = "file";
        const accepted = this.options.acceptedFiles;
        if (accepted) input.accept = accepted;
        input.multiple =
          this.options.maxFiles == null || this.options.maxFiles > 1;
        input.style.display = "none";
        input.onchange = (e) => {
          const files = Array.from(e.target.files || []);
          if (!files.length) {
            try {
              document.body.removeChild(input);
            } catch (_) {
              /* ignore */
            }
            return;
          }
          // Push directo a dropzone.files (sin emit "addedfile") + a
          // previewFiles + notify al padre. El watcher `files` se va a
          // disparar (porque el padre va a actualizar indication.files),
          // pero filtra archivos que ya están en dropzone — no entra al
          // loop. Equivalente a `dz.addFile` pero sin disparar el event
          // "addedfile" interno de Dropzone.
          const dz = this.$refs.uploader && this.$refs.uploader.dropzone;
          files.forEach((file) => {
            try {
              if (dz && Array.isArray(dz.files)) {
                dz.files.push(file);
              }
              this.previewFiles.push(file);
              this.$emit("file-added", file, this.previewFiles);
            } catch (err) {
              // eslint-disable-next-line no-console
              console.error("[FileUpload] add file error:", err);
              this.$emit("fileupload-error", {
                file,
                message: err.message,
                xhr: null,
              });
            }
          });
          this.emitUpdatedFiles();
          try {
            document.body.removeChild(input);
          } catch (_) {
            /* ignore */
          }
        };
        document.body.appendChild(input);
        input.click();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[FileUpload] openFileDialog error:", err);
        this.$message.error("Error al abrir el selector de archivos");
      }
    },

    /**
     * @description Encola archivos pendientes y dispara el procesamiento de
     *              la queue (Dropzone). BUG-Z2V55Y: el caller original llamaba
     *              esto sin manejo de error y enqueueFiles/processQueue pueden
     *              freezar el main thread con archivos grandes. Wrappeado en
     *              try/catch para que cualquier excepción durante el enqueue
     *              no deje el componente en estado inconsistente y emita
     *              fileupload-error al padre para que muestre feedback.
     */
    startUpload() {
      try {
        const { files = [] } = this.$refs.uploader.dropzone;
        const filesToQueue = files.filter((f) => !f.key);
        if (filesToQueue.length === 0) {
          this.$emit("queue-complete");
          return;
        }
        this.$refs.uploader.dropzone.enqueueFiles(filesToQueue);
        setTimeout(() => {
          try {
            this.$refs.uploader.processQueue();
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error("[FileUpload] processQueue error:", err);
            this.$emit("fileupload-error", {
              file: null,
              message: err.message,
              xhr: null,
            });
          }
        }, 0);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[FileUpload] startUpload enqueue error:", err);
        this.$message.error("Error al iniciar la subida del archivo");
        this.$emit("fileupload-error", {
          file: null,
          message: err.message,
          xhr: null,
        });
      }
    },

    onQueueCompleted() {
      this.$emit("queue-complete");
    },

    onSuccess() {
      this.$emit("fileupload-success");
    },

    onError(file, message, xhr) {
      file && this.$refs.uploader.removeFile(file);
      if (message !== this.options.dictUploadCanceled) {
        this.$message.error(message);
      }
      this.$emit("fileupload-error", { file, message, xhr });
    },

    onFileAdded(file) {
      if (!file.key) {
        this.previewFiles.push(file);
        this.$emit("file-added", file, this.previewFiles);
        this.emitUpdatedFiles();
      }
    },

    onFileDeleted(deletedFile) {
      this.$emit("file-removed", deletedFile, this.previewFiles);

      const { files = [] } = this.$refs.uploader.dropzone;
      if (!files.length) {
        this.$emit("files-removed");
        this.previewFiles = [];
        this.emitUpdatedFiles();
      }
    },

    addFiles(files = []) {
      files.forEach((file) => this.addFile(file, true)); // force = true para archivos existentes
    },

    addFile(file, force = false) {
      const fileKey = file.key || file.value || file.path;
      const fileName = file.originalName || this.getFileName(fileKey);
      const url = `${configService.s3Url}/${fileKey}`;

      const dz = this.$refs.uploader.dropzone;

      // Si force = true, eliminar el archivo existente primero
      if (force) {
        const existingFile = dz.files.find((f) => f.key === fileKey);
        if (existingFile) {
          dz.removeFile(existingFile);
        }
      }

      const isAdded = dz.files.some((f) => f.key === fileKey);
      const canAdd =
        !this.options.maxFiles || this.options.maxFiles > dz.files.length;

      if (!isAdded && canAdd) {
        const mockFile = {
          size: file.size,
          name: fileName,
          type: file.mimeType,
          key: fileKey,
        };

        // Usar emit en lugar de manuallyAddFile
        dz.emit("addedfile", mockFile);
        dz.emit("thumbnail", mockFile, url);
        dz.emit("complete", mockFile);
        dz.files.push(mockFile);

        // Agregar a previewFiles para que el watcher no lo elimine
        if (fileKey) {
          this.previewFiles.push(mockFile);
        }
      }
    },

    deleteFile(file) {
      const index = this.previewFiles.findIndex(
        (f) =>
          f.name === file.name && f.size === file.size && f.type === file.type
      );
      if (index !== -1) {
        this.previewFiles.splice(index, 1);
        this.$refs.uploader.removeFile(file);
        this.emitUpdatedFiles(); // ✅ reflejar cambios
      }
    },

    emitUpdatedFiles() {
      this.$emit(
        "update:files",
        this.previewFiles.map((f) => ({
          key: f.key,
          size: f.size,
          mimeType: f.type,
          originalName: f.name,
        }))
      );
    },

    getFileName(key) {
      return key?.split("/").pop() || "";
    },

    truncateFileName(name) {
      return name?.length > 45 ? name.substring(0, 45) + "..." : name;
    },
  },

  computed: {
    showDropzone() {
      return this.mode === "dropzone";
    },
  },
};
</script>

<template lang="pug">
  .file-upload-wrapper
    vue-dropzone#dropzone(
      v-show="showDropzone"
      ref="uploader"
      :options="options"
      :duplicateCheck="true"
      @vdropzone-error="onError"
      @vdropzone-success="onSuccess"
      @vdropzone-file-added="onFileAdded"
      @vdropzone-removed-file="onFileDeleted"
      @vdropzone-queue-complete="onQueueCompleted"
    )
    span(v-if="!showDropzone")
      button.button-blue-new.long(@click="openFileDialog" :disabled="disabled") Adjuntar archivos
      .selected-container(v-for="file in previewFiles" :key="file.key")
        .selection-item__long
          span {{ truncateFileName(file.name) }}
          span {{ (file.size / (1024 * 1024)).toFixed(2) }} MB
          span {{ file.type.split('/').pop().toUpperCase() }}
          button.button-invisible-new(@click="deleteFile(file)" :disabled="disabled")
            micon(name="clear" @click="deleteFile(file)" :disabled="disabled")
  </template>

<style lang="scss" scoped>
.file-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 10px;

  .file-details {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 12px;

    p {
      color: $steel-blue;
    }
  }
}

.file-upload-wrapper {
  margin: 12px 0;
}
</style>
